import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import "./AllProductsExplore.css";
import { useTranslation } from "react-i18next";
import { useProductsExploreSection } from "../../../../hooks/usePublicQueries";

import { productExploreCategories } from "../../../../data/productExploreCategories";
import { resolveMediaUrl } from "../../../../utils/media";

const fallbackExplore = {
  tag: "OUR PRODUCTS",
  titleLine1: "Explore all",
  titleLine2: "products categories",
};

const itemFieldRegex = /^item(\d+)([a-zA-Z0-9_]+)$/i;

const pickString = (...candidates) => {
  for (const c of candidates) {
    if (typeof c === "string" && c.trim()) return c.trim();
  }
  return "";
};

const hasSectionData = (section) => {
  if (!section || typeof section !== "object") return false;
  return Object.values(section).some((value) => {
    if (value === null || typeof value === "undefined") return false;
    if (typeof value === "string") return value.trim().length > 0;
    return true;
  });
};

const tryParseJsonObject = (str) => {
  const t = String(str || "").trim();
  if (!t.startsWith("{") || !t.endsWith("}")) return null;
  try {
    const o = JSON.parse(t);
    return o && typeof o === "object" && !Array.isArray(o) ? o : null;
  } catch {
    return null;
  }
};

const coalesceExplorePayload = (raw) => {
  if (raw == null) return null;
  if (typeof raw === "string") return tryParseJsonObject(raw) || null;
  if (typeof raw !== "object") return null;
  let base = { ...raw };
  if (raw.data && typeof raw.data === "object" && !Array.isArray(raw.data)) {
    base = { ...raw, ...raw.data };
  }
  if (typeof raw.value === "string") {
    const parsed = tryParseJsonObject(raw.value);
    if (parsed) base = { ...base, ...parsed };
  } else if (raw.value && typeof raw.value === "object" && !Array.isArray(raw.value)) {
    base = { ...base, ...raw.value };
  }
  return base;
};

const normalizeSectionSlug = (key) =>
  String(key || "")
    .toLowerCase()
    .replace(/[_\s-]+/g, "");

const getItemIconFromApi = (content, itemNumber) => {
  const suffix = String(itemNumber);
  const candidates = [
    content?.[`item${suffix}Icon`],
    content?.[`item${suffix}icon`],
    content?.[`item${suffix}Image`],
    content?.[`item${suffix}image`],
    content?.[`item${suffix}Media`],
    content?.[`item${suffix}media`],
    content?.[`item${suffix}MediaUrl`],
    content?.[`item${suffix}mediaUrl`],
  ];
  for (const candidate of candidates) {
    const resolved = resolveMediaUrl(candidate);
    if (resolved) return resolved;
  }
  return "";
};

const AllProductsExplore = ({ content }) => {
  const { i18n } = useTranslation();
  const shouldFetch = !(content && typeof content === "object");
  const { data: exploreSection } = useProductsExploreSection(i18n.language, {
    enabled: shouldFetch,
  });
  const apiContent = shouldFetch ? exploreSection : null;

  const section = useMemo(() => {
    const source = content ?? apiContent;
    const merged = coalesceExplorePayload(source);
    return hasSectionData(merged) ? merged : fallbackExplore;
  }, [content, apiContent]);

  const categories = useMemo(() => {
    const cardsMap = new Map();
    Object.entries(section || {}).forEach(([key, value]) => {
      const match = key.match(itemFieldRegex);
      if (!match) return;
      const index = Number(match[1]);
      const field = String(match[2] || "").replace(/[_\s-]+/g, "").toLowerCase();
      if (!cardsMap.has(index)) cardsMap.set(index, {});
      cardsMap.get(index)[field] = value;
    });

    const mapped = Array.from(cardsMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([index, fields], idx) => {
        const fallback = productExploreCategories[idx % productExploreCategories.length];
        const title = pickString(fields.title, fields.heading) || fallback.title;
        const normalizedTitle = title.toLowerCase().trim();
        const mappedLink = pickString(fields.link, fields.to, fields.url) || fallback.to;
        const to =
          normalizedTitle === "drywall metal frame"
            ? "/products/drywall-metal-frame"
            : normalizedTitle === "ceiling metal frame"
            ? "/products/ceiling-metal-frame"
            : mappedLink;
        return {
          title,
          description: pickString(fields.description, fields.text, fields.body) || fallback.description,
          icon:
            getItemIconFromApi(section, index) ||
            resolveMediaUrl(fields.icon || fields.image || fields.media || fields.mediaurl) ||
            fallback.icon,
          to,
          fallbackIcon: fallback.icon,
        };
      })
      .filter((item) => item.title || item.description || item.icon || item.to);

    return mapped.length
      ? mapped
      : productExploreCategories.map((item) => ({ ...item, fallbackIcon: item.icon }));
  }, [section]);

  const titleLine1 = pickString(section.titleLine1);
  const titleLine2 = pickString(section.titleLine2);
  const singleTitle = pickString(section.title, section.heading);

  return (
    <section className="allProductsExplore" aria-labelledby="allProductsExplore-heading">
      <div className="allProductsExplore-inner">
        <div className="allProductsExplore-header">
          <span className="allProductsExplore-tag">
            {pickString(section.tag, section.sectionTag) || fallbackExplore.tag}
          </span>

          <h2 id="allProductsExplore-heading" className="allProductsExplore-title">
            {titleLine1 || (singleTitle ? singleTitle : fallbackExplore.titleLine1)}{" "}
            <span className="allProductsExplore-titleAccent">
              {titleLine2 || (!singleTitle ? fallbackExplore.titleLine2 : "")}
            </span>
          </h2>
        </div>

        <div className="allProductsExplore-grid">
          {categories.map((item) => {
            const inner = (
              <>
                <div className="allProductsExplore-cardTop">
                  <div className="allProductsExplore-icon">
                    <img
                      src={item.icon}
                      alt=""
                      draggable={false}
                      onError={(e) => {
                        if (e.currentTarget.src !== item.fallbackIcon) {
                          e.currentTarget.src = item.fallbackIcon;
                        }
                      }}
                    />
                  </div>
                </div>
                <div className="allProductsExplore-cardBody">
                  <h3 className="allProductsExplore-cardTitle">{item.title}</h3>
                  <p className="allProductsExplore-cardDesc">{item.description}</p>
                </div>
              </>
            );

            return item.to ? (
              <Link className="allProductsExplore-card" key={item.title} to={item.to}>
                {inner}
              </Link>
            ) : (
              <div className="allProductsExplore-card" key={item.title}>
                {inner}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default AllProductsExplore;
